import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { catchError, EMPTY, map, Observable, Subscription, tap } from "rxjs";
import { Cryptocoin } from "@models";
import { containsString } from "@helpers/utils";
import { CoreModule } from "@modules/core/core.module";
import { ModalService } from "@core-services/modal.service";
import { CryptocoinService } from "@main-services/currency";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { AddressBookWalletFacade } from "../services/address-book-wallet.facade";
import { AddressBookWalletService } from "../services/address-book-wallet.service";
import {
	AddressBookWalletHostExtra,
	AddressBookWalletHostModel,
	AddressBookWalletHostType,
	AddressBookWalletModel,
	AddressBookWalletModelAsset,
	AddressBookWalletModelNetwork,
	AddressBookWalletPayload,
	AddressBookWalletViewType,
	OwnerType,
	ProfileType,
	WalletType
} from "../model/address-book-wallet.model";
import { NewProfileFacade } from "../../profile/new-profile/state/new-profile.facade";
import { ProfileModel } from "../../profile/new-profile/entities/profile-model";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { config } from "@config";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { SegmentService } from "@main-services/segment.service";
import { UserService } from "@main-services/user.service";
import * as levenshtein from "damerau-levenshtein";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "app-address-book-create-wallet-person-type-modal",
	templateUrl: "./address-book-create-wallet-person-type-modal.component.html",
	styleUrls: ["./address-book-create-wallet-person-type-modal.component.scss"],
	providers: [NewProfileFacade],
	imports: [CoreModule, ReactiveFormsModule]
})
export class AddressBookCreateWalletPersonTypeModalComponent implements OnInit, OnDestroy {
	@Input() set data({
		type,
		wallet,
		accountWalletId,
		selectedNetwork,
		selectedCryptocoin,
		ownerType,
		walletType,
		SHW3rdPartyDisabled
	}) {
		this.modalType = type;
		this.accountWalletId = accountWalletId;
		this.withdrawSelectedNetwork = selectedNetwork;
		this.withdrawSelectedCryptocoin = selectedCryptocoin;
		this.ownerType = ownerType;
		this.walletType = walletType;
		this.wallet = wallet; // Store the wallet data for LEGACY mode
		this.SHW3rdPartyDisabled = SHW3rdPartyDisabled;

		// Set the asset and network if we have them from previous flow
		if (selectedCryptocoin && selectedNetwork) {
			this.selectedCryptocoin = selectedCryptocoin;
			this.selectedNetwork = selectedNetwork;
		}

		this.setModalContent();
		this.setupFormValidation();

		// Handle LEGACY mode data prefilling
		if (this.modalType === AddressBookWalletViewType.LEGACY && wallet) {
			this.prefillLegacyData(wallet);
		}
	}

	public assetOptions$: Observable<BpSelectOption[]> = EMPTY;
	public networkOptions$: Observable<BpSelectOption[]> = EMPTY;
	public walletHosts$: Observable<BpSelectOption[]> = EMPTY;
	public shouldDisplayDestinationTag: boolean = false;
	public destinationTagText: string;
	public destinationTagTextOptional: string;
	public modalTitle: string = "create_new_wallet";
	public modalDescription: string = "create_new_wallet_withdraw_details";
	public modalType: AddressBookWalletViewType = AddressBookWalletViewType.NEW;
	public ownerType?: OwnerType;
	public walletType?: WalletType;
	public addressBookWalletViewType = AddressBookWalletViewType;
	public walletTypeEnum = WalletType;
	public ownerTypeEnum = OwnerType;
	public learnMoreUrl: string = config.externalPaths["travel_rule"]["faq"];
	public selectedNetwork: AddressBookWalletModelNetwork = null;
	public selectedCryptocoin: AddressBookWalletModelAsset = null;
	public walletAddressError: string;
	public hostHintError: string;
	public walletOwnerError: string;
	public destinationTagError: string;
	public legalEntityIdentifierError: string;

	private accountWalletId?: string;
	private selectedWalletHostId: string;
	private profileNameSubscription: Subscription = Subscription.EMPTY;
	private addNewWalletSubscription: Subscription = Subscription.EMPTY;
	private filterHostsSubscription: Subscription = Subscription.EMPTY;
	private profileName: string = "";
	private selectedAssetNetworks: BpSelectOption[] = [];
	private withdrawSelectedNetwork: Network;
	private withdrawSelectedCryptocoin: PaymentsCryptocoin;
	private wallet?: AddressBookWalletModel; // Store wallet data for LEGACY mode
	private walletId: string; // Store wallet ID for deletion
	public SHW3rdPartyDisabled: boolean;

	public walletForm: FormGroup = new FormGroup({
		walletOwner: new FormControl<string>("", Validators.required),
		walletAddress: new FormControl<string>("", Validators.required),
		asset: new FormControl<string>(""),
		network: new FormControl<string>(""),
		walletHost: new FormControl<string>(""),
		otherHostName: new FormControl<string>(""),
		ownWallet: new FormControl<boolean>(false),
		destinationTag: new FormControl<string>(""),
		selfDeclared: new FormControl<boolean>(false),
		legalEntityIdentifier: new FormControl<string>("")
	});

	// Form control getters
	public get walletOwner(): AbstractControl<string> {
		return this.walletForm.get("walletOwner");
	}

	public get asset(): AbstractControl<string> {
		return this.walletForm.get("asset");
	}

	public get network(): AbstractControl<string> {
		return this.walletForm.get("network");
	}

	public get walletHost(): AbstractControl<string> {
		return this.walletForm.get("walletHost");
	}

	private get ownWallet(): AbstractControl<boolean> {
		return this.walletForm.get("ownWallet");
	}

	public get selfDeclared(): AbstractControl<boolean> {
		return this.walletForm.get("selfDeclared");
	}

	public get destinationTag(): AbstractControl<string> {
		return this.walletForm.get("destinationTag");
	}

	public get walletAddress(): AbstractControl<string> {
		return this.walletForm.get("walletAddress");
	}

	public get otherHostName(): AbstractControl<string> {
		return this.walletForm.get("otherHostName");
	}

	public get legalEntityIdentifier(): AbstractControl<string> {
		return this.walletForm.get("legalEntityIdentifier");
	}

	// Computed properties for form field visibility
	public get shouldShowAssetNetwork(): boolean {
		return this.modalType === AddressBookWalletViewType.NEW || this.modalType === AddressBookWalletViewType.LEGACY;
	}

	public get shouldShowWalletHost(): boolean {
		return this.walletType === WalletType.EXCHANGE;
	}

	public get shouldShowLegalEntityIdentifier(): boolean {
		return this.ownerType === OwnerType.LEGAL_ENTITY;
	}

	public get shouldShowSelfDeclaration(): boolean {
		return this.SHW3rdPartyDisabled === false;
	}

	public get shouldShowSelfHostedWarning(): boolean {
		return this.walletType === WalletType.SELF_HOSTED;
	}

	public get ownerFieldLabel(): string {
		return this.ownerType === OwnerType.LEGAL_ENTITY
			? "address_book_wallet_data_business_name_label"
			: "new_address_book_full_legal_name";
	}

	public get ownerFieldPlaceholder(): string {
		return this.ownerType === OwnerType.LEGAL_ENTITY
			? "address_book_wallet_data_business_name_placeholder"
			: "new_address_book_full_legal_name";
	}

	public get ownerFieldHint(): string {
		return this.ownerType === OwnerType.LEGAL_ENTITY ? "" : "new_address_book_full_legal_name_helper";
	}

	public get ownWalletSwitchLabel(): string {
		if (this.ownerType === OwnerType.LEGAL_ENTITY) {
			return "the_wallet_belongs_to_my_business";
		}

		// Check if user is business and we're in person flow
		if (this.getProfileType() === ProfileType.Business && this.ownerType === OwnerType.PERSON) {
			return "the_wallet_belongs_to_my_business";
		}

		return "the_wallet_belongs_to_me";
	}

	public get shouldShowOwnWalletSwitch(): boolean {
		if (this.SHW3rdPartyDisabled && this.walletType === WalletType.SELF_HOSTED) {
			return false;
		}

		// RULE 2: If user is regular and we're in 'legal entity' flow, hide the switch
		if (this.getProfileType() === ProfileType.Regular && this.ownerType === OwnerType.LEGAL_ENTITY) {
			return false;
		}

		// RULE 2: If user is business and we're in 'person' flow, hide the switch
		if (this.getProfileType() === ProfileType.Business && this.ownerType === OwnerType.PERSON) {
			return false;
		}

		// For regular users or business users in legal entity flow, show the switch
		return true;
	}

	public get legalEntityIdentifierPlaceholder(): string {
		return this.translationService.translate("address_book_wallet_data_lei_number_placeholder");
	}

	protected readonly walletFacade = inject(AddressBookWalletFacade);

	constructor(
		private readonly modalService: ModalService,
		private readonly cryptocoinService: CryptocoinService,
		private readonly walletService: AddressBookWalletService,
		private readonly profileFacade: NewProfileFacade,
		private readonly translationService: TranslationService,
		private readonly infoService: InfoService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly userService: UserService
	) {}

	ngOnInit(): void {
		// Load asset options for NEW mode OR LEGACY mode
		if (this.shouldShowAssetNetwork) {
			this.assetOptions$ = this.walletFacade.getAssetOptions();
		} else {
			// For WITHDRAW mode, we already have the asset and network
			if (this.selectedCryptocoin && this.selectedNetwork) {
				this.asset.setValue(this.selectedCryptocoin.id);
				this.network.setValue(this.selectedNetwork.coin_network_id);
				const destinationTag = this.selectedNetwork.destination_tag
					? this.selectedNetwork.destination_tag.type
					: undefined;
				this.handleDestinationTagDisplay(this.selectedNetwork.coin_network_id, destinationTag);
			}
		}

		// Only load wallet hosts for exchange wallets
		if (this.walletType === WalletType.EXCHANGE) {
			this.walletHosts$ = this.walletFacade.getWalletHostsOptions().pipe(
				map((hosts: BpSelectOption<AddressBookWalletHostModel>[]) => [
					...hosts,
					{
						name: this.translationService.translate("other_host"),
						value: AddressBookWalletHostExtra.OTHER
					}
				])
			);
		}

		this.profileNameSubscription = this.profileFacade.getProfile$().subscribe(({ fullName }: ProfileModel) => {
			this.profileName = fullName;
			if (this.SHW3rdPartyDisabled && this.walletType === WalletType.SELF_HOSTED) {
				this.onOwnWalletChange(true);
			}
		});

		this.segmentService.track({
			name: "Address Book Add Wallet Person Information Screen Viewed"
		});
		this.rudderstackService.track({
			name: "Address Book Add Wallet Person Information Screen Viewed"
		});
	}

	private getProfileType(): string {
		if (this.userService.user.isBusinessUser()) {
			return ProfileType.Business;
		}
		return ProfileType.Regular;
	}

	private setupFormValidation(): void {
		// Asset and network validation for NEW mode
		if (this.modalType === AddressBookWalletViewType.NEW) {
			this.asset.setValidators([Validators.required]);
			this.network.setValidators([Validators.required]);
		}

		// Wallet host validation for exchange wallets
		if (this.walletType === WalletType.EXCHANGE) {
			this.walletHost.setValidators([Validators.required]);
		}

		// Update all validators
		this.asset.updateValueAndValidity();
		this.network.updateValueAndValidity();
		this.walletHost.updateValueAndValidity();
		this.legalEntityIdentifier.updateValueAndValidity();
	}

	public onAssetChange(selectedAssetValue: string): void {
		this.networkOptions$ = this.walletFacade.getNetworksForSelectedCoin(selectedAssetValue).pipe(
			tap((networks: BpSelectOption[]) => {
				this.selectedAssetNetworks = networks;
				if (this.selectedAssetNetworks.length === 1) {
					this.onNetworkChange(this.selectedAssetNetworks[0].value);
				}
			})
		);
		this.asset.setValue(selectedAssetValue);
		this.shouldDisplayDestinationTag = false;
		this.walletAddress.setErrors(null);
	}

	public onNetworkChange(selectedNetworkValue: string): void {
		this.network.setValue(selectedNetworkValue);
		this.handleDestinationTagDisplay(selectedNetworkValue);
		this.walletAddress.setErrors(null);
	}

	public onWalletHostChange(selectedWalletHost: string): void {
		this.filterHostsSubscription = this.walletHosts$.subscribe({
			next: (hosts) => {
				let updatedWalletHostId = null;
				for (const host of hosts) {
					if (host.name === selectedWalletHost) {
						updatedWalletHostId = host?.["wallet_host_id"];
						break;
					}
				}
				this.selectedWalletHostId = updatedWalletHostId;
			}
		});
		this.walletHost.setValue(selectedWalletHost);
	}

	public onOwnWalletChange(isOwnWallet: boolean): void {
		this.ownWallet.setValue(isOwnWallet);

		if (isOwnWallet) {
			this.walletOwner.disable();
			this.walletOwner.setValue(this.profileName);
		} else {
			this.walletOwner.enable();
			this.walletOwner.reset();
			this.walletOwner.setValidators([Validators.required]);
		}

		this.walletOwner.updateValueAndValidity();
	}

	public onSelfDeclaredChange(selfDeclared: boolean): void {
		this.selfDeclared.setValue(selfDeclared);
	}

	public back(): void {
		this.modalService.pop();
	}

	public cancel(): void {
		this.modalService.popAll();
	}

	public saveWallet(): void {
		this.handleAddingNewWallet();
	}

	public deleteWallet(): void {
		this.walletFacade.deleteWalletById(this.walletId);
	}

	public getCustomSearch(): Function {
		return (searchInput: string, { details: id }: BpSelectOption) => {
			const coin: Cryptocoin = this.getCoinFromFieldId(id);

			if (!coin) {
				return false;
			}
			if (containsString(coin.getSymbol(), searchInput)) {
				return true;
			}
			return containsString(coin.getName(), searchInput);
		};
	}

	public getHostSearch(): Function {
		return (searchInput: string, { name, value }: BpSelectOption): boolean => {
			const option: string = name.toLocaleLowerCase();
			const input: string = searchInput.toLocaleLowerCase();
			const lev = levenshtein(option, input);
			return (
				(lev.steps < 7 && lev.similarity > 0.49) ||
				option.includes(input) ||
				Object.values(AddressBookWalletHostExtra).includes(value)
			);
		};
	}

	private getCoinFromFieldId(id: string): Cryptocoin {
		return this.cryptocoinService.getItemById(id);
	}

	private handleDestinationTagDisplay(selectedNetworkValue: string, destinationTag?: string): void {
		let selectedNetworkDestinationTag = "";
		if (destinationTag) {
			selectedNetworkDestinationTag = destinationTag;
		} else {
			const selectedNetwork: BpSelectOption = this.selectedAssetNetworks.find(
				({ value }: BpSelectOption) => value === selectedNetworkValue
			);
			selectedNetworkDestinationTag = selectedNetwork?.details;
		}
		this.shouldDisplayDestinationTag = !!selectedNetworkDestinationTag;
		if (this.shouldDisplayDestinationTag) {
			let tag: string;
			switch (selectedNetworkDestinationTag) {
				case "message":
					tag = "message_tag";
					break;
				case "memo":
					tag = "stellar_memo";
					break;
				default:
					tag = "destination_tag";
			}
			this.destinationTagText = this.translationService.translate(tag);
			this.destinationTagTextOptional = this.translationService.translate("destination_tag_optional", [
				this.destinationTagText
			]);
		}
	}

	private setModalContent(): void {
		this.modalTitle = "create_new_wallet";
		this.modalDescription = "create_new_wallet_withdraw_details";
	}

	private prefillLegacyData(wallet: AddressBookWalletModel): void {
		// Store the wallet ID for deletion
		this.walletId = wallet.id;

		// Prefill and disable asset/network if available
		if (wallet?.asset) {
			this.walletFacade.getCoinByCryptocoinName(wallet.asset.name).subscribe((coin) => {
				if (coin) {
					this.asset.setValue(coin.id);
					this.asset.disable();

					if (wallet?.network) {
						const coinNetwork = coin.networks.find((item) => item.name.toString() === wallet.network.name);
						if (coinNetwork) {
							this.network.setValue(coinNetwork.coin_network_id);
							this.network.disable();
						}
					}
				}
			});
		}

		// Prefill and disable wallet address
		if (wallet?.wallet_address) {
			this.walletAddress.setValue(wallet.wallet_address);
			this.walletAddress.disable();
		}

		// Prefill and disable destination tag
		if (wallet?.wallet_destination_tag) {
			this.destinationTag.setValue(wallet.wallet_destination_tag);
			this.destinationTag.disable();
		}

		// Prefill owner name
		if (wallet?.owner_legal_name) {
			this.walletOwner.setValue(wallet.owner_legal_name);
		}

		// Prefill legal entity identifier if available
		if (wallet?.legal_entity_identifier) {
			this.legalEntityIdentifier.setValue(wallet.legal_entity_identifier);
		}

		// Set wallet host if available
		if (wallet?.wallet_host_name) {
			this.walletHost.setValue(wallet.wallet_host_name);
		}

		// Set user_owned status
		if (wallet?.user_owned !== undefined) {
			this.ownWallet.setValue(wallet.user_owned);
			this.onOwnWalletChange(wallet.user_owned);
		}

		// Update modal content for legacy mode
		this.modalTitle = "edit_wallet";
		this.modalDescription = "complete_missing_details";
	}

	private handleAddingNewWallet(): void {
		this.segmentService.track({
			name: "Address Book Add Wallet Person Save Button Tapped"
		});
		this.rudderstackService.track({
			name: "Address Book Add Wallet Person Save Button Tapped"
		});

		const addNewWalletPayload: AddressBookWalletPayload = {
			coin_network_id: this.network.value,
			user_owned: this.ownWallet.value,
			wallet_address: this.walletAddress.value,
			wallet_owner_type: this.ownerType,
			...(this.walletType === WalletType.EXCHANGE
				? {
						wallet_host_name:
							this.walletHost.value !== AddressBookWalletHostExtra.OTHER ? this.walletHost.value : null,
						wallet_host_hint:
							this.walletHost.value === AddressBookWalletHostExtra.OTHER
								? this.otherHostName.value
								: null,
						wallet_host_type: AddressBookWalletHostType.VASP
					}
				: {
						wallet_host_name: null,
						wallet_host_hint: null,
						wallet_host_type: AddressBookWalletHostType.SELF_HOSTED
					}),
			// Handle owner_legal_name based on owner type and user_owned
			...(this.ownerType === OwnerType.LEGAL_ENTITY
				? {
						// For legal entity: null if my business, business name if not my business
						owner_legal_name: this.ownWallet.value ? null : this.walletOwner.value,
						...(this.legalEntityIdentifier.value && {
							legal_entity_identifier: this.legalEntityIdentifier.value
						})
					}
				: {
						// For natural person: use owner name if not own wallet
						...(!this.ownWallet.value && { owner_legal_name: this.walletOwner.value })
					}),
			...(this.selectedWalletHostId && { wallet_host_id: this.selectedWalletHostId }),
			...(this.shouldDisplayDestinationTag &&
				this.destinationTag.value && { wallet_destination_tag: this.destinationTag.value })
		};

		if (this.shouldShowSelfDeclaration) {
			addNewWalletPayload.self_declared = this.selfDeclared.value;
		} else {
			addNewWalletPayload.self_declared = true;
		}

		if (this.modalType === AddressBookWalletViewType.LEGACY) {
			this.addNewWalletSubscription = this.walletService
				.editLegacyWallet(this.walletId, addNewWalletPayload)
				.pipe(
					catchError((err) => {
						const { title, code, field } = err.responseErrors[0].meta[0];
						const errorMessage: string = title;
						this.infoService.setError(errorMessage);
						this.handleInlineError(code, field);
						return EMPTY;
					})
				)
				.subscribe((createdWallet: AddressBookWalletModel) => {
					if (createdWallet.user_owned && !createdWallet.owner_legal_name) {
						createdWallet = this.walletFacade.updateOwnerName(createdWallet);
					}
					this.walletFacade.displayViewWalletModal(createdWallet);
					this.closeAddNewWalletModal();
				});
		} else {
			this.addNewWalletSubscription = this.walletService
				.addNewWallet(addNewWalletPayload)
				.pipe(
					catchError((err) => {
						const { title, code, field } = err.responseErrors[0].meta[0];
						const errorMessage: string = title;
						this.infoService.setError(errorMessage);
						this.handleInlineError(code, field);
						return EMPTY;
					})
				)
				.subscribe((createdWallet: AddressBookWalletModel) => {
					if (createdWallet.user_owned && !createdWallet.owner_legal_name) {
						createdWallet = this.walletFacade.updateOwnerName(createdWallet);
					}
					if (this.modalType !== AddressBookWalletViewType.WITHDRAW) {
						this.walletFacade.displayViewWalletModal(createdWallet);
					} else {
						this.walletFacade.validateWalletToSend(
							this.accountWalletId,
							createdWallet,
							this.withdrawSelectedNetwork,
							this.withdrawSelectedCryptocoin
						);
					}
					this.closeAddNewWalletModal();
				});
		}
	}

	private handleInlineError(errorCode: string, field: string): void {
		const errorMapper: Map<string, string> = new Map([
			["validation.exception.wallet_address.characters", "address_book_validation_address_invalid_characters"],
			["validation.exception.wallet_address.length", "address_book_validation_address_too_many"],
			["validation.exception.wallet_address.address_invalid", "address_book_validation_address_invalid"],
			["validation.exception.wallet_address.not_unique", "address_book_validation_address_already_saved"],
			["validation.exception.wallet_host_hint.length", "address_book_validation_hint_too_many"],
			["validation.exception.owner_legal_name.length", "address_book_validation_owner_name_too_many"],
			["validation.exception.wallet_destination_tag.invalid", "address_book_validation_destination_tag_invalid"],
			["validation.exception.legal_entity_identifier.required", "address_book_validation_lei_required"],
			["validation.exception.legal_entity_identifier.length", "address_book_validation_lei_length"],
			["validation.exception.legal_entity_identifier.characters", "address_book_validation_lei_characters"]
		]);

		if (field === "wallet_address") {
			this.walletAddress.setErrors({ invalid_address: true });
			this.walletAddressError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_address_generic");
		}

		if (field === "wallet_host_hint") {
			this.otherHostName.setErrors({ invalid_host_hint: true });
			this.hostHintError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_hint_generic");
		}

		if (field === "owner_legal_name") {
			this.walletOwner.setErrors({ invalid_owner_name: true });
			this.walletOwnerError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_owner_name_generic");
		}

		if (field === "wallet_destination_tag") {
			this.destinationTag.setErrors({ invalid_destination_tag: true });
			this.destinationTagError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode), [this.destinationTagText])
				: this.translationService.translate("address_book_validation_destination_tag_generic", [
						this.destinationTagText
					]);
		}

		if (field === "legal_entity_identifier") {
			this.legalEntityIdentifier.setErrors({ invalid_lei: true });
			this.legalEntityIdentifierError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_lei_generic");
		}
	}

	private closeAddNewWalletModal(): void {
		this.infoService.setSuccess(this.translationService.translate("wallet_saved"));
		this.walletService.shouldRefreshWallets.next(true);
		this.modalService.pop();
	}

	ngOnDestroy(): void {
		this.profileNameSubscription.unsubscribe();
		this.addNewWalletSubscription.unsubscribe();
		this.filterHostsSubscription.unsubscribe();
	}
}
